import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { organizationsApi, type Organization } from '@/lib/api/organizations';
import { enrollmentsApi, type EnrollmentToken } from '@/lib/api/enrollments';

// Mounted only while open; the one-time secret stays out of query caches/stores.
export function EnrollmentTokenDialog({ onClose }: { onClose: () => void }) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationId, setOrganizationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [issued, setIssued] = useState<EnrollmentToken | null>(null);
  const [expired, setExpired] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let active = true;
    void organizationsApi
      .list()
      .then((items) => {
        if (!active) return;
        const manageable = items.filter((item) => item.role === 'owner' || item.role === 'manager');
        setOrganizations(manageable);
        setOrganizationId(manageable[0]?.id || '');
      })
      .catch(() => {
        if (active)
          setError('No se pudieron cargar tus organizaciones. Cerrá e intentá nuevamente.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!issued) return;
    const timer = window.setTimeout(
      () => {
        setExpired(true);
        setIssued(null);
        setCopied(false);
      },
      Math.max(0, Date.parse(issued.expiresAt) - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [issued]);
  const generate = async () => {
    setBusy(true);
    setError('');
    setExpired(false);
    setCopied(false);
    try {
      setIssued(await enrollmentsApi.createOrganizationToken(organizationId));
    } catch {
      setError(
        'No se pudo generar el código. Verificá que seguís teniendo permiso para gestionar la organización.'
      );
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    if (!issued || Date.parse(issued.expiresAt) <= Date.now()) {
      setExpired(true);
      setIssued(null);
      return;
    }
    try {
      await navigator.clipboard.writeText(issued.enrollmentToken);
      setCopied(true);
    } catch {
      setError('No se pudo copiar. Seleccioná y copiá el código manualmente.');
    }
  };
  return (
    <Dialog open onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Agregar equipo a una organización</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>
            Generá un código para una instalación nueva. Después revisá y aprobá la solicitud del
            equipo en esta pantalla.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {expired && <Alert severity="warning">El código venció. Generá uno nuevo.</Alert>}
          {!loading && !error && organizations.length === 0 && (
            <Alert severity="info">
              Necesitás ser propietario o administrador de una organización para agregar equipos.
            </Alert>
          )}
          <TextField
            select
            label="Organización"
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
            disabled={loading || busy || !!issued}
            fullWidth
          >
            {organizations.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          {issued && (
            <>
              <TextField
                label="Código de instalación"
                value={issued.enrollmentToken}
                slotProps={{ input: { readOnly: true } }}
                fullWidth
              />
              <Typography variant="body2" color="text.secondary">
                Se usa en una sola instalación y vence a las{' '}
                {new Date(issued.expiresAt).toLocaleTimeString()}. Al cerrar, el código deja de
                mostrarse.
              </Typography>
              <Button onClick={() => void copy()}>{copied ? 'Copiado' : 'Copiar código'}</Button>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cerrar
        </Button>
        {!issued && (
          <Button
            variant="contained"
            onClick={() => void generate()}
            disabled={loading || busy || !organizationId}
          >
            {busy ? 'Generando...' : 'Generar código'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
